/**
 * Changelog utility functions for parsing and displaying release notes
 */

/**
 * Extract the first image URL from markdown content.
 * Matches the markdown image syntax: ![alt text](url)
 *
 * @param markdown - The markdown string to parse
 * @returns The first image URL found, or null if none exists
 */
export const extractFirstImageUrl = (markdown: string): string | null => {
  if (!markdown) return null;

  const imageRegex = /!\[.*?\]\((https?:\/\/[^\s)]+)\)/;
  const match = imageRegex.exec(markdown);
  return match?.[1] ?? null;
};

/**
 * Check if a date string is within the specified number of days from today.
 * Used to determine if a changelog entry is recent enough to display.
 *
 * @param dateString - ISO date string to check
 * @param thresholdDays - Maximum age in days
 * @returns true if the date is within the threshold, false otherwise
 */
export const isWithinThreshold = (
  dateString: string,
  thresholdDays: number
): boolean => {
  if (!dateString || thresholdDays <= 0) return false;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;

  const thresholdDate = new Date();
  thresholdDate.setDate(thresholdDate.getDate() - thresholdDays);
  return date > thresholdDate;
};

/**
 * Truncate text to a specified maximum length, adding ellipsis if truncated.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 80)
 * @returns The truncated text with ellipsis, or the original text if within limit
 */
export const truncateText = (text: string, maxLength: number = 80): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

/**
 * Default threshold in days for displaying changelog entries
 */
export const DEFAULT_THRESHOLD_DAYS = 30;

/**
 * Legacy localStorage key for storing the last seen changelog entry ID.
 * Keep this around so users who dismissed an entry before the seen-list
 * migration do not see that same entry again.
 */
export const CHANGELOG_LAST_SEEN_KEY = "changelog_last_seen_entry_id";

/**
 * localStorage key for storing changelog entry IDs the user dismissed or opened.
 */
export const CHANGELOG_SEEN_ENTRY_IDS_KEY = "changelog_seen_entry_ids";

/**
 * Maximum number of seen changelog entry IDs to keep in localStorage.
 */
export const MAX_SEEN_CHANGELOG_ENTRY_IDS = 50;

/**
 * Canny label names for product-specific changelog entries
 */
export const CHANGELOG_LABELS = {
  GAIL_GPT: "GailGPT",
  GAIL_AGENT: "GailAgent",
} as const;

/**
 * Check if a changelog entry should be shown to the user based on its labels
 * and the user's product access.
 *
 * - If entry has no product labels (neither GailGPT nor GailAgent), show to all
 * - If entry has GailGPT label, only show to users with GailGPT access
 * - If entry has GailAgent label, only show to users with GailAgent access
 *
 * @param labels - Array of label names from the changelog entry
 * @param userCanUseGailGpt - Whether the user has GailGPT access
 * @param userCanUseGailAgent - Whether the user has GailAgent access
 * @returns true if the entry should be shown to the user
 */
export const shouldShowEntryForUser = (
  labels: string[],
  userCanUseGailGpt: boolean,
  userCanUseGailAgent: boolean
): boolean => {
  const hasGailGptLabel = labels.includes(CHANGELOG_LABELS.GAIL_GPT);
  const hasGailAgentLabel = labels.includes(CHANGELOG_LABELS.GAIL_AGENT);

  // If no product-specific labels, show to everyone
  if (!hasGailGptLabel && !hasGailAgentLabel) {
    return true;
  }

  // If has GailGPT label, user must have GailGPT access
  if (hasGailGptLabel && userCanUseGailGpt) {
    return true;
  }

  // If has GailAgent label, user must have GailAgent access
  if (hasGailAgentLabel && userCanUseGailAgent) {
    return true;
  }

  return false;
};

/**
 * Parse persisted seen changelog entry IDs and merge in the legacy single ID.
 */
export const parseSeenChangelogEntryIds = (
  storedSeenEntryIds: string | null,
  legacyLastSeenEntryId?: string | null
): string[] => {
  const seenEntryIds: string[] = [];

  if (storedSeenEntryIds) {
    try {
      const parsed = JSON.parse(storedSeenEntryIds);

      if (Array.isArray(parsed)) {
        seenEntryIds.push(
          ...parsed.filter(
            (entryId): entryId is string =>
              typeof entryId === "string" && entryId.length > 0
          )
        );
      }
    } catch {
      // Invalid localStorage data should not break the release notes widget.
    }
  }

  if (legacyLastSeenEntryId) {
    seenEntryIds.push(legacyLastSeenEntryId);
  }

  return Array.from(new Set(seenEntryIds));
};

/**
 * Add an entry ID to the front of the seen list while preserving recency order.
 */
export const addSeenChangelogEntryId = (
  seenEntryIds: string[],
  entryId: string
): string[] => {
  if (!entryId) return seenEntryIds.slice(0, MAX_SEEN_CHANGELOG_ENTRY_IDS);

  return [
    entryId,
    ...seenEntryIds.filter((seenEntryId) => seenEntryId !== entryId),
  ].slice(0, MAX_SEEN_CHANGELOG_ENTRY_IDS);
};

/**
 * Check if a changelog entry is recent and has not already been seen.
 */
export const shouldShowChangelogEntry = (
  entry: { id: string; publishedAt: string },
  seenEntryIds: string[],
  thresholdDays: number
): boolean => {
  const seenEntryIdSet = new Set(seenEntryIds);

  return (
    !seenEntryIdSet.has(entry.id) &&
    isWithinThreshold(entry.publishedAt, thresholdDays)
  );
};
